import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import publicApi from '../services/public.api';
import { useLocation } from '../context/LocationContext';
import { useCart } from '../context/CartContext';
import { addToCartAnimated } from '../utils/addToCartAnimated';
import ProductCardQuantity, { getDefaultCardSelection, isProductHasVariants } from './ProductCardQuantity';
import { buildCartProductFromSelection } from '../utils/cartProductHelpers';

const RAMADANESQUE_CATEGORY_ID = 7;

const OrganicOne = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addedProductId, setAddedProductId] = useState(null);
    const [selections, setSelections] = useState({});
    const [weightHintId, setWeightHintId] = useState(null);
    const { formatPrice, isCasaRegion } = useLocation();
    const { addToCart } = useCart();

    // Get the correct price based on location
    const getProductPrice = (product) => {
        if (isCasaRegion && product.price_casa) {
            return parseFloat(product.price_casa);
        }
        return parseFloat(product.price || 0);
    };

    // Calculate discounted price
    const getDiscountedPrice = (product) => {
        const basePrice = getProductPrice(product);
        const discount = parseFloat(product.discount || 0);
        if (discount > 0) {
            return basePrice * (1 - discount / 100);
        }
        return basePrice;
    };

    const getSelection = (product) =>
        selections[product.id] || getDefaultCardSelection(isProductHasVariants(product));

    const updateSelection = (product, next) => {
        setSelections((prev) => ({ ...prev, [product.id]: next }));
    };

    const handleAddToCart = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        const built = buildCartProductFromSelection(
            product,
            getDiscountedPrice(product),
            getSelection(product)
        );
        if (built.error) {
            setWeightHintId(product.id);
            setTimeout(() => setWeightHintId(null), 2000);
            return;
        }
        addToCartAnimated({
            event: e,
            product: built.product,
            addToCart,
            quantity: built.quantity,
        });
        setAddedProductId(product.id);
        setTimeout(() => setAddedProductId(null), 1500);
    };

    function SampleNextArrow(props) {
        const { className, onClick } = props;
        return (
            <button
                type="button" onClick={onClick}
                className={` ${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-right" />
            </button>
        );
    }
    function SamplePrevArrow(props) {
        const { className, onClick } = props;

        return (
            <button
                type="button"
                onClick={onClick}
                className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-left" />
            </button>
        );
    }

    const settings = {
        dots: false,
        arrows: true,
        infinite: true,
        speed: 1000,
        slidesToShow: 6,
        slidesToScroll: 1,
        initialSlide: 0,
        autoplay: true,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: 1599,
                settings: {
                    slidesToShow: 6,
                },
            },
            {
                breakpoint: 1399,
                settings: {
                    slidesToShow: 4,
                },
            },
            {
                breakpoint: 992,
                settings: {
                    slidesToShow: 3,
                },
            },
            {
                breakpoint: 575,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 424,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    };

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await publicApi.getProductsByCategory(RAMADANESQUE_CATEGORY_ID);
                if (response.success && response.data) {
                    // Filter to show only products WITHOUT offers/discounts
                    const productsWithoutOffers = response.data.filter(p => parseFloat(p.discount || 0) === 0);
                    setProducts(productsWithoutOffers);
                }
            } catch (error) {
                console.error('Error fetching Ramadanesque products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Get image URL
    const getImageUrl = (imagePath) => {
        return publicApi.getImageUrl(imagePath);
    };

    // Don't render if no products
    if (!loading && products.length === 0) {
        return null;
    }

    return (
        <section className="organic-food py-80">
            <div className="container container-lg">
                <div className="section-heading">
                    <div className="flex-between flex-wrap gap-8">
                        <h5 className="mb-0">Ramadanesque</h5>
                        <div className="flex-align mr-point gap-16">
                            <Link
                                to="/shop"
                                className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                            >
                                Toutes les Catégories
                            </Link>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-main" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                ) : (
                    <div className="organic-food__slider arrow-style-two">
                        <Slider {...settings}>
                            {products.map((product) => (
                                <div key={product.id} style={{ padding: '0 8px' }}>
                                    <div 
                                        className="product-card organic-product-hover px-8 py-16 border border-gray-100 rounded-16 position-relative transition-2"
                                        style={{ 
                                            height: '100%', 
                                            minHeight: '420px',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                        {product.discount > 0 && (
                                            <span 
                                                className="product-card__badge hot-deals-badge px-8 py-4 text-sm"
                                                style={{ backgroundColor: '#eab802', color: '#000', width: '30%' }}
                                            >
                                                -{product.discount}%
                                            </span>
                                        )}
                                        <Link
                                            to={`/product-details/${product.id}`}
                                            className="product-card__thumb flex-center"
                                            style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <img 
                                                src={getImageUrl(product.image)} 
                                                alt={product.name}
                                                style={{ maxHeight: '180px', maxWidth: '180px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                    e.target.src = 'assets/images/thumbs/productr1.png';
                                                }}
                                            />
                                        </Link>
                                        <div className="product-card__content mt-12" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <div className="flex-align gap-6">
                                                <span className="text-xs fw-bold text-gray-500">
                                                    {parseFloat(product.rating_average || 0).toFixed(1)}
                                                </span>
                                                <span className="text-15 fw-bold text-warning-600 d-flex">
                                                    <i className="ph-fill ph-star" />
                                                </span>
                                                <span className="text-xs fw-bold text-gray-500">
                                                    ({product.rating_count || 0})
                                                </span>
                                            </div>
                                            <h6 className="title text-lg fw-semibold mt-12 mb-8">
                                                <Link to={`/product-details/${product.id}`} className="link text-line-2">
                                                    {product.name}
                                                </Link>
                                            </h6>
                                            <div className="flex-align gap-4">
                                                <span className="text-main-600 text-md d-flex">
                                                    <i className="ph-fill ph-storefront" />
                                                </span>
                                                <span className="text-gray-500 text-xs">
                                                    Groupe Délices Benkirane
                                                </span>
                                            </div>
                                            <div className="flex-between gap-8 mt-24 flex-wrap align-items-center" style={{ marginTop: 'auto' }}>
                                                <div className="product-card__price">
                                                    {product.discount > 0 ? (
                                                        <>
                                                            <span className="text-gray-400 text-md fw-semibold text-decoration-line-through d-block">
                                                                {formatPrice(getProductPrice(product))} DH
                                                            </span>
                                                            <span className="text-heading text-md fw-semibold ">
                                                                {formatPrice(getDiscountedPrice(product))} DH <span className="text-gray-500 fw-normal">/Qty</span>
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="text-heading text-md fw-semibold ">
                                                            {formatPrice(getProductPrice(product))} DH <span className="text-gray-500 fw-normal">/Qty</span>
                                                        </span>
                                                    )}
                                                    {weightHintId === product.id && (
                                                        <span className="text-danger text-xs fw-medium d-block mt-4">
                                                            Choisissez un poids
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="d-flex align-items-center gap-8 flex-wrap">
                                                    <ProductCardQuantity
                                                        hasVariants={isProductHasVariants(product)}
                                                        selection={getSelection(product)}
                                                        onChange={(next) => updateSelection(product, next)}
                                                        collapsedUntilInteraction
                                                    />
                                                    <button
                                                        onClick={(e) => handleAddToCart(e, product)}
                                                        disabled={addedProductId === product.id}
                                                        className={`product-card__cart btn ${
                                                            addedProductId === product.id 
                                                                ? 'btn-success' 
                                                                : 'organic-btn'
                                                        } text-white hover-text-white py-11 px-24 rounded-pill flex-align gap-8`}
                                                        style={{
                                                            transition: 'all 0.3s ease',
                                                            border: 'none',
                                                            cursor: addedProductId === product.id ? 'default' : 'pointer'
                                                        }}
                                                    >
                                                        {addedProductId === product.id ? (
                                                            <>Ajouté <i className="ph ph-check-circle" /></>
                                                        ) : (
                                                            <>Ajouter <i className="ph ph-shopping-cart" /></>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>
                )}
            </div>
        </section>
    )
}

export default OrganicOne
